// Pure, dependency-free helpers behind the Segment Builder page: the
// condition-row draft model and its round-trip to/from wire `SegmentRule`s,
// the field picker (catalog minus legacy, coming-soon rows disabled), the
// operator lists per row type, per-row validation, the audience-preview
// sample line, and the "Start from a template" presets. No React, no
// gateway — safe to unit-test and to import from anywhere.

import type {
  SegmentCustomerSample,
  SegmentRule,
  SegmentRuleField,
  SegmentRuleOp,
} from '../../../types/loyaltySegments'
import {
  SEGMENT_EVENT_DEFS,
  SEGMENT_EVENT_OPS,
  SEGMENT_FIELD_DEFS,
  SEGMENT_FIELD_GROUPS,
  SEGMENT_LEGACY_EVENT_OPS,
  SEGMENT_LEGACY_NUMERIC_OPS,
  SEGMENT_LEGACY_SELECT_OPS,
  SEGMENT_NUMERIC_OPS,
  SEGMENT_SELECT_OPS,
  type SegmentEventDef,
  type SegmentFieldGroup,
  type SegmentSelectOpDef,
  segmentEventDef,
  segmentEventDefForRule,
  segmentFieldDef,
} from '../../../types/loyaltySegments'

// Server bounds (segment_criteria.MAX_WINDOW_DAYS / MAX_KEYWORD_LENGTH).
export const MAX_WINDOW_DAYS = 3650
export const MAX_KEYWORD_LENGTH = 100

// ── Draft model ──────────────────────────────────────────────────────────────

export type EventMode = 'has_done' | 'has_not_done'

// Monotonic id for a condition row's React key — stable identity independent
// of the row's position, so deleting/reordering rows above it doesn't cause
// React to reuse a DOM node (and its subcomponents' local hover/focus state)
// across two logically different rows. `crypto.randomUUID` is preferred
// (collision-proof across builder instances); the counter only covers
// environments without it.
let draftIdCounter = 0
const nextDraftId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `condition-${++draftIdCounter}`

export interface ConditionDraft {
  // Stable identity for the React key — see `nextDraftId`. Not sent to the
  // server; `draftToRule` never reads it.
  id: string
  // 'event' rows are the mockup's "Has done / Has not done" conditions.
  kind: 'field' | 'event'
  // Field rows: the catalog field. Event rows: 'event_count' (or, for a
  // stored coupon / points event, the field the server rewrote it onto —
  // unused on save, which always sends event_count + event).
  field: SegmentRuleField
  op: SegmentRuleOp
  // Numeric value (or '1'/'0' for bool fields) kept as the raw input string.
  value: string
  // Upper bound for `between`.
  value2: string
  // Selected chips for in/nin fields.
  values: (string | number)[]
  // Select rows: "is any of" (many chips) vs "is" / "is not" (one chip).
  selectMulti: boolean
  // "in the last… days" — '' means all time.
  windowDays: string
  // Event rows: the SEGMENT_EVENT_DEFS key; '' until the user picks one.
  eventKey: string
  eventMode: EventMode
  // Event rows loaded from a legacy stored rule whose metric doesn't map to
  // any alias — kept verbatim so re-saving never drops the rule.
  metric: string
  // "containing …" keyword filter (events that support it).
  keyword: string
}

const fieldType = (field: string) => segmentFieldDef(field)?.type ?? 'number'

// Mockup default for a fresh row: Total Orders · is greater than.
export const DEFAULT_CONDITION_FIELD: SegmentRuleField = 'order_count'

export const emptyDraftForField = (field: SegmentRuleField): ConditionDraft => {
  const type = fieldType(field)
  return {
    id: nextDraftId(),
    kind: 'field',
    field,
    op: type === 'select' ? 'in' : type === 'bool' ? 'eq' : 'gt',
    value: type === 'bool' ? '1' : '',
    value2: '',
    values: [],
    selectMulti: false,
    windowDays: '',
    eventKey: '',
    eventMode: 'has_done',
    metric: '',
    keyword: '',
  }
}

// "in the last N days" a fresh event row starts with. Seeded as a real value
// (not just the input's placeholder) so preview/save send what the row shows.
export const DEFAULT_EVENT_WINDOW_DAYS = '30'

export const emptyDraftForEvent = (
  eventMode: EventMode,
  eventKey = '',
): ConditionDraft => ({
  ...emptyDraftForField('event_count'),
  kind: 'event',
  op: 'gte',
  value: '1',
  windowDays: DEFAULT_EVENT_WINDOW_DAYS,
  eventKey,
  eventMode,
})

export const emptyDraft = (): ConditionDraft =>
  emptyDraftForField(DEFAULT_CONDITION_FIELD)

// ── Rule ⇄ draft ─────────────────────────────────────────────────────────────

// The event definition a stored rule renders as: the `event` alias first,
// then (legacy rules) the metric lookup, then a promo_order_count rule that
// carries a coupon keyword — only the old Redeemed Coupon row produced those.
const eventDefForStoredRule = (
  rule: SegmentRule,
): SegmentEventDef | undefined =>
  segmentEventDefForRule(rule) ??
  (rule.field === 'promo_order_count' && rule.keyword
    ? segmentEventDef('redeemed_coupon')
    : undefined)

// Edit mode must round-trip every stored shape without data loss: legacy
// numeric rules, select rules (one chip => "is"/"is not", more => "is any
// of"), and event rules back into the has-done row they were built as
// (`eq 0` is "has not done"). An event_count rule whose metric is unknown
// still becomes an event row (metric kept) rather than vanishing.
// "Has not done X <qualifier> N times" is the negation of the has-done rule:
//   not (count >= N)  ->  count < N      (at least N)
//   not (count <= N)  ->  count > N      (at most N)
//   not (count == N)  ->  count != N     (exactly N)
// "Has not done X at least 1 time" is the plain "never did X", stored in its
// canonical zero-count form (eq 0) so older rules and the server presets
// round-trip unchanged.
const NEGATED_EVENT_OP: Partial<Record<SegmentRuleOp, SegmentRuleOp>> = {
  gte: 'lt',
  lte: 'gt',
  eq: 'neq',
}
const HAS_NOT_DONE_QUALIFIER: Partial<Record<SegmentRuleOp, SegmentRuleOp>> = {
  lt: 'gte',
  gt: 'lte',
  neq: 'eq',
}

// The (mode, qualifier, count) a stored event rule was authored as.
export const eventModeForRule = (
  rule: Pick<SegmentRule, 'op' | 'value'>,
): { eventMode: EventMode; op: SegmentRuleOp; value: number } => {
  const count = Number(rule.value ?? 0)
  if (rule.op === 'eq' && count === 0) {
    return { eventMode: 'has_not_done', op: 'gte', value: 1 }
  }
  const qualifier = HAS_NOT_DONE_QUALIFIER[rule.op]
  if (qualifier)
    return { eventMode: 'has_not_done', op: qualifier, value: count }
  return { eventMode: 'has_done', op: rule.op, value: count }
}

export const ruleToDraft = (rule: SegmentRule): ConditionDraft => {
  const type = fieldType(rule.field)
  const eventDef = eventDefForStoredRule(rule)
  const isEvent = Boolean(eventDef) || rule.field === 'event_count'
  const values = rule.values ?? []
  const authored = isEvent ? eventModeForRule(rule) : null
  return {
    id: nextDraftId(),
    kind: isEvent ? 'event' : 'field',
    field: rule.field,
    op: authored ? authored.op : rule.op,
    value: authored
      ? String(authored.value)
      : rule.value != null
        ? String(rule.value)
        : type === 'bool'
          ? '1'
          : '',
    value2: rule.value2 != null ? String(rule.value2) : '',
    values,
    selectMulti: values.length !== 1,
    windowDays: rule.window_days != null ? String(rule.window_days) : '',
    eventKey: eventDef?.key ?? '',
    eventMode: authored?.eventMode ?? 'has_done',
    metric:
      !eventDef && rule.field === 'event_count' ? (rule.metric ?? '') : '',
    keyword: rule.keyword ?? '',
  }
}

const windowPart = (draft: ConditionDraft): Pick<SegmentRule, 'window_days'> =>
  draft.windowDays.trim() !== ''
    ? { window_days: Number(draft.windowDays) }
    : {}

export const draftToRule = (draft: ConditionDraft): SegmentRule => {
  if (draft.kind === 'event') {
    // Every event goes out as event_count + alias; the server resolves it
    // (abandoned_cart is derived there — never send a metric for it). "Has
    // done at least/at most/exactly N"; "has not done …" is its negation
    // (NEGATED_EVENT_OP), with "not at least 1" as the canonical eq 0.
    const eventDef = segmentEventDef(draft.eventKey)
    const hasNotDone = draft.eventMode === 'has_not_done'
    const count = Number(draft.value)
    const negated =
      hasNotDone && draft.op === 'gte' && count <= 1
        ? { op: 'eq' as SegmentRuleOp, value: 0 }
        : hasNotDone
          ? { op: NEGATED_EVENT_OP[draft.op] ?? 'lt', value: count }
          : { op: draft.op, value: count }
    return {
      field: 'event_count',
      op: negated.op,
      value: negated.value,
      ...(!hasNotDone && draft.op === 'between'
        ? { value2: Number(draft.value2) }
        : {}),
      ...(eventDef ? { event: eventDef.key } : { metric: draft.metric }),
      ...windowPart(draft),
      ...(eventDef?.keyword && draft.keyword.trim()
        ? { keyword: draft.keyword.trim() }
        : {}),
    }
  }
  const def = segmentFieldDef(draft.field)
  const type = def?.type ?? 'number'
  if (type === 'bool') {
    return { field: draft.field, op: 'eq', value: draft.value === '0' ? 0 : 1 }
  }
  if (type === 'select') {
    return {
      field: draft.field,
      op: draft.op,
      values: draft.values,
      ...(def?.window ? windowPart(draft) : {}),
    }
  }
  return {
    field: draft.field,
    op: draft.op,
    value: Number(draft.value),
    ...(draft.op === 'between' ? { value2: Number(draft.value2) } : {}),
    ...(def?.window ? windowPart(draft) : {}),
  }
}

// ── Validation ───────────────────────────────────────────────────────────────

const windowError = (draft: ConditionDraft): string | null => {
  if (draft.windowDays.trim() === '') return null
  const windowDays = Number(draft.windowDays)
  if (!Number.isInteger(windowDays) || windowDays < 1) {
    return 'Window must be a whole number of days (1+)'
  }
  if (windowDays > MAX_WINDOW_DAYS) {
    return `Window must be at most ${MAX_WINDOW_DAYS} days`
  }
  return null
}

// Mirrors server validation: numbers ≥ 0, between needs value2 ≥ value, in/nin
// need ≥ 1 chip, event rows need an event, window is a positive integer.
export const conditionError = (draft: ConditionDraft): string | null => {
  if (draft.kind === 'event') {
    if (!draft.eventKey && !draft.metric) return 'Select an event'
    {
      const count = Number(draft.value)
      if (draft.value.trim() === '' || !Number.isInteger(count) || count < 0) {
        return 'Enter a whole number of times (0+)'
      }
      if (draft.eventMode === 'has_done' && draft.op === 'between') {
        const count2 = Number(draft.value2)
        if (
          draft.value2.trim() === '' ||
          !Number.isInteger(count2) ||
          count2 < count
        ) {
          return 'Upper count must be at least the lower count'
        }
      }
    }
    if (draft.keyword.length > MAX_KEYWORD_LENGTH) {
      return `Keyword must be ${MAX_KEYWORD_LENGTH} characters or less`
    }
    return windowError(draft)
  }
  const def = segmentFieldDef(draft.field)
  if (def?.comingSoon) return 'This condition is coming soon'
  const type = def?.type ?? 'number'
  if (type === 'bool') return null
  if (type === 'select') {
    return draft.values.length > 0 ? null : 'Pick at least one option'
  }
  const value = Number(draft.value)
  if (draft.value.trim() === '' || !Number.isFinite(value) || value < 0) {
    return 'Enter a value of 0 or more'
  }
  if (draft.op === 'between') {
    const value2 = Number(draft.value2)
    if (draft.value2.trim() === '' || !Number.isFinite(value2) || value2 < 0) {
      return 'Enter an upper value of 0 or more'
    }
    if (value2 < value) return 'Upper value must be at least the lower value'
  }
  return def?.window ? windowError(draft) : null
}

// ── Field picker ─────────────────────────────────────────────────────────────

// Values the row's field <select> carries for the two event rows.
export const EVENT_PICKER_VALUE: Record<EventMode, string> = {
  has_done: 'event:has_done',
  has_not_done: 'event:has_not_done',
}

export const eventModeForPickerValue = (value: string): EventMode | null =>
  value === EVENT_PICKER_VALUE.has_done
    ? 'has_done'
    : value === EVENT_PICKER_VALUE.has_not_done
      ? 'has_not_done'
      : null

export const pickerValueForDraft = (draft: ConditionDraft): string =>
  draft.kind === 'event' ? EVENT_PICKER_VALUE[draft.eventMode] : draft.field

export interface SegmentPickerOption {
  value: string
  label: string
  disabled?: boolean
  hint?: string
}

export interface SegmentPickerGroup {
  label: string
  options: SegmentPickerOption[]
}

// The "Add condition" field picker: the sheet's three groups in order (legacy
// rows hidden, coming-soon rows disabled) plus the Events group. A row whose
// current field is legacy — or unknown to this catalog — still lists it, in
// its own group, so an existing rule renders and edits with its label.
export const segmentFieldPickerGroups = (
  current?: ConditionDraft,
): SegmentPickerGroup[] => {
  const groups: SegmentPickerGroup[] = SEGMENT_FIELD_GROUPS.map((group) => ({
    label: group,
    options: SEGMENT_FIELD_DEFS.filter(
      (def) => def.group === group && !def.legacy,
    ).map((def) => ({
      value: def.field,
      // Coming-soon fields keep their plain label (a suffix made the dropdown
      // wide); they're greyed out via `disabled` and explained by the hint.
      label: def.label,
      disabled: def.comingSoon || undefined,
      hint: def.hint,
    })),
  }))

  if (current && current.kind === 'field') {
    const def = segmentFieldDef(current.field)
    if (def?.legacy) {
      const groupLabel: SegmentFieldGroup = def.group
      const group = groups.find((g) => g.label === groupLabel)
      const option = { value: def.field, label: def.label, hint: def.hint }
      if (group) group.options.push(option)
      else groups.push({ label: groupLabel, options: [option] })
    } else if (!def) {
      groups.push({
        label: 'Other',
        options: [{ value: current.field, label: current.field }],
      })
    }
  }

  groups.push({
    label: 'Events',
    options: [
      { value: EVENT_PICKER_VALUE.has_done, label: 'Has done' },
      { value: EVENT_PICKER_VALUE.has_not_done, label: 'Has not done' },
    ],
  })
  return groups
}

// The event <select> on an event row: the sheet's events, plus the legacy
// metric a stored rule carries when it maps to none of them.
export const LEGACY_METRIC_PREFIX = 'metric:'

export const eventPickerOptions = (
  draft: ConditionDraft,
): SegmentPickerOption[] => {
  const options: SegmentPickerOption[] = SEGMENT_EVENT_DEFS.map((def) => ({
    value: def.key,
    label: def.label,
  }))
  if (!draft.eventKey && draft.metric) {
    options.push({
      value: `${LEGACY_METRIC_PREFIX}${draft.metric}`,
      label: `${draft.metric} (legacy)`,
    })
  }
  return options
}

export const eventPickerValue = (draft: ConditionDraft): string =>
  draft.eventKey ||
  (draft.metric ? `${LEGACY_METRIC_PREFIX}${draft.metric}` : '')

// ── Operator lists ───────────────────────────────────────────────────────────

export interface SegmentOpOption {
  value: SegmentRuleOp
  label: string
}

// Number fields: the sheet's six operators; a legacy op (`neq`) is listed
// only while it's the row's current op so the row never shows a blank.
export const numericOpOptions = (
  currentOp?: SegmentRuleOp,
): SegmentOpOption[] => [
  ...SEGMENT_NUMERIC_OPS,
  ...SEGMENT_LEGACY_NUMERIC_OPS.filter((o) => o.value === currentOp),
]

// Event rows: at least / at most / exactly, plus the current legacy qualifier.
export const eventOpOptions = (
  currentOp?: SegmentRuleOp,
): SegmentOpOption[] => [
  ...SEGMENT_EVENT_OPS,
  ...SEGMENT_LEGACY_EVENT_OPS.filter((o) => o.value === currentOp),
]

// Select fields: is / is not / is any of, plus "is none of" while a legacy
// multi-value negation is the row's state. Option identity is (op, multi), so
// the <select> carries the index.
export const selectOpOptions = (
  draft?: ConditionDraft,
): SegmentSelectOpDef[] => [
  ...SEGMENT_SELECT_OPS,
  ...SEGMENT_LEGACY_SELECT_OPS.filter(
    (o) => draft && o.value === draft.op && o.multi === draft.selectMulti,
  ),
]

export const selectOpIndex = (draft: ConditionDraft): number =>
  selectOpOptions(draft).findIndex(
    (o) => o.value === draft.op && o.multi === draft.selectMulti,
  )

// Operator choices for any row, by its type.
export const operatorOptionsFor = (
  draft: ConditionDraft,
): SegmentOpOption[] => {
  if (draft.kind === 'event') return eventOpOptions(draft.op)
  const type = fieldType(draft.field)
  if (type === 'select') {
    return selectOpOptions(draft).map(({ value, label }) => ({ value, label }))
  }
  if (type === 'bool') return [{ value: 'eq', label: 'is' }]
  return numericOpOptions(draft.op)
}

// ── Audience preview sample line ─────────────────────────────────────────────

const moneyFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
})

// Newer servers may put spend / order figures on the sample; the typed
// contract doesn't promise them, so they're read structurally.
type SampleWithStats = SegmentCustomerSample & {
  lifetime_value?: number | null
  order_count?: number | null
}

export const sampleName = (customer: SegmentCustomerSample): string => {
  const name = `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
  return name || customer.email || 'Unknown customer'
}

export const sampleInitials = (customer: SegmentCustomerSample): string => {
  const first = (customer.first_name ?? '').trim()
  const last = (customer.last_name ?? '').trim()
  if (first || last)
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?'
  return (customer.email ?? '?').slice(0, 2).toUpperCase()
}

// "$1,240 LTV · 9 orders" when the sample carries both figures; otherwise the
// email (or '' when the name already is the email).
export const sampleMeta = (customer: SegmentCustomerSample): string => {
  const stats = customer as SampleWithStats
  if (stats.lifetime_value != null && stats.order_count != null) {
    const orders = Number(stats.order_count)
    return `$${moneyFormatter.format(Number(stats.lifetime_value))} LTV · ${orders} order${
      orders === 1 ? '' : 's'
    }`
  }
  const email = customer.email ?? ''
  return email && email !== sampleName(customer) ? email : ''
}

// ── Templates ────────────────────────────────────────────────────────────────

// "Start from a template": the nine Audience Segments presets the server
// seeds (segment_service.PRESET_SEGMENTS, v2), so a custom variant starts from
// the same rules as the default it resembles.
export interface SegmentTemplate {
  name: string
  description: string
  color: string
  match: 'all' | 'any'
  rules: SegmentRule[]
}

export const SEGMENT_TEMPLATES: SegmentTemplate[] = [
  {
    name: 'VIP',
    description:
      'Top 10% of customers by lifetime value. At least 3 orders and $500+ spent. These are your most valuable advocates.',
    color: '#7C3AED',
    match: 'all',
    rules: [
      { field: 'lifetime_value', op: 'gte', value: 500 },
      { field: 'order_count', op: 'gte', value: 3 },
      { field: 'last_seen_days', op: 'lte', value: 30 },
    ],
  },
  {
    name: 'New Customers',
    description:
      'Customers whose first order was placed within the last 30 days. Key window for building loyalty and repeat habits.',
    color: '#1E7D4A',
    match: 'all',
    rules: [
      { field: 'joined_days', op: 'lte', value: 30 },
      { field: 'order_count', op: 'eq', value: 1 },
    ],
  },
  {
    name: 'Loyal Regulars',
    description:
      'Consistent shoppers with 6 or more orders who are active on the site. Your most reliable revenue base.',
    color: '#1D4ED8',
    match: 'all',
    rules: [
      { field: 'order_count', op: 'gte', value: 6 },
      { field: 'last_order_days', op: 'lte', value: 35 },
      {
        field: 'event_count',
        event: 'completed_purchase',
        op: 'gte',
        value: 3,
        window_days: 90,
      },
    ],
  },
  {
    name: 'At Risk',
    description:
      "Previously active customers who haven't purchased in 45–90 days. Still reachable — the right offer can bring them back.",
    color: '#B45309',
    match: 'all',
    rules: [
      { field: 'order_count', op: 'gte', value: 2 },
      { field: 'last_order_days', op: 'between', value: 45, value2: 90 },
      {
        field: 'event_count',
        event: 'completed_purchase',
        op: 'eq',
        value: 0,
        window_days: 45,
      },
    ],
  },
  {
    name: 'Lapsed',
    description:
      "Customers who were once active but haven't placed an order in over 90 days. Win-back campaigns perform best here.",
    color: '#DC2626',
    match: 'all',
    rules: [
      { field: 'last_order_days', op: 'gt', value: 90 },
      { field: 'order_count', op: 'gte', value: 2 },
      { field: 'last_seen_days', op: 'gt', value: 60 },
    ],
  },
  {
    name: 'High Spenders',
    description:
      'Customers whose average order value exceeds $150. May not purchase frequently but drive significant revenue per visit.',
    color: '#EA580C',
    match: 'all',
    rules: [
      { field: 'aov', op: 'gte', value: 150 },
      { field: 'order_count', op: 'gte', value: 2 },
    ],
  },
  {
    name: 'First-Time Buyers',
    description:
      "Customers with exactly one purchase who haven't returned within 60 days. Early intervention is critical for conversion.",
    color: '#0F766E',
    match: 'all',
    rules: [
      { field: 'order_count', op: 'eq', value: 1 },
      { field: 'joined_days', op: 'between', value: 15, value2: 60 },
    ],
  },
  {
    name: 'Discount Seekers',
    description:
      'Customers who used promo codes on 60%+ of their orders. Margin-sensitive — best targeted with loyalty rewards.',
    color: '#4B5563',
    match: 'all',
    rules: [
      { field: 'promo_orders_pct', op: 'gte', value: 60 },
      {
        field: 'event_count',
        event: 'redeemed_coupon',
        op: 'gte',
        value: 3,
        window_days: 90,
      },
    ],
  },
  {
    name: 'Inactive',
    description:
      'Customers with no purchase in over 180 days and no recent site activity. Low re-engagement probability.',
    color: '#4B5563',
    match: 'all',
    rules: [
      { field: 'last_order_days', op: 'gt', value: 180 },
      { field: 'last_seen_days', op: 'gt', value: 180 },
      { field: 'order_count', op: 'gte', value: 1 },
    ],
  },
]

// Key-order-insensitive rule comparison.
const canonicalRule = (rule: SegmentRule): string =>
  JSON.stringify(
    Object.fromEntries(
      Object.entries(rule)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  )

export const rulesEqual = (a: SegmentRule, b: SegmentRule): boolean =>
  canonicalRule(a) === canonicalRule(b)

// Sanity check a template against the catalog: every rule must be a
// picker-offered field (not legacy / coming soon) or a known event alias,
// with an offered operator, and must survive the draft round-trip unchanged.
// Returns the problems found ([] when valid).
export const templateProblems = (template: SegmentTemplate): string[] => {
  const problems: string[] = []
  template.rules.forEach((rule, index) => {
    const label = `${template.name} rule ${index + 1}`
    if (rule.field === 'event_count') {
      if (!rule.event || !segmentEventDef(rule.event)) {
        problems.push(`${label}: unknown event ${String(rule.event)}`)
      }
      if (rule.metric) problems.push(`${label}: must not send a metric`)
      if (!SEGMENT_EVENT_OPS.some((o) => o.value === rule.op)) {
        problems.push(`${label}: op ${rule.op} is not an event qualifier`)
      }
    } else {
      const def = segmentFieldDef(rule.field)
      if (!def || def.legacy || def.comingSoon) {
        problems.push(`${label}: field ${rule.field} is not offered`)
      } else if (def.type === 'select') {
        if (!SEGMENT_SELECT_OPS.some((o) => o.value === rule.op)) {
          problems.push(`${label}: op ${rule.op} is not a select operator`)
        }
      } else if (!SEGMENT_NUMERIC_OPS.some((o) => o.value === rule.op)) {
        problems.push(`${label}: op ${rule.op} is not a number operator`)
      }
      if (rule.window_days != null && !def?.window) {
        problems.push(`${label}: ${rule.field} does not accept a window`)
      }
    }
    const draft = ruleToDraft(rule)
    const error = conditionError(draft)
    if (error) problems.push(`${label}: ${error}`)
    if (!rulesEqual(draftToRule(draft), rule)) {
      problems.push(`${label}: does not round-trip through the builder`)
    }
  })
  return problems
}
